import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {SubdomainClaimService} from "../gen/kitecorp/transferify/storefront/v1/subdomain_claim_pb";
import {subdomainClaimOf} from "./customDomainMessages";

/**
 * The sender's claimed brand subdomain (kitecorp/transferify-api-service#350).
 *
 * Separate from {@link AccountBrandApi} because the surfaces have different gates: branding is refused to
 * a non-entitled sender on every verb, whereas reading and RELEASING a claim stay open on every tier so a
 * downgraded sender can still see and give back the name they hold.
 */

/** The `SubdomainClaimService` claim. */
export type SubdomainClaim = {
	/** The claimed label, without any brand suffix, e.g. `acme`. */
	label: string;
	/**
	 * Every brand origin this claim serves recipient links on, e.g. `https://acme.transferify.cloud` and
	 * `https://acme.transferify.ro`. Server-derived: the client must never compose a brand suffix, or a new
	 * brand (and the dev hosts) would show the wrong address here.
	 */
	hosts: string[];
	/** ISO-8601 instant the label was claimed. */
	claimedAt: string;
};

const claimClient = () => createClient(SubdomainClaimService, apiTransport);

export class AccountSubdomainApi {
	/** The current claim, or `null` when nothing is claimed: an answer with no claim, or one with no label (ui#534). */
	static async getClaim(): Promise<SubdomainClaim | null> {
		return subdomainClaimOf((await claimClient().getSubdomainClaim({})).claim);
	}

	static async claim(label: string): Promise<SubdomainClaim | null> {
		return subdomainClaimOf((await claimClient().claimSubdomain({label})).claim);
	}

	static async release(): Promise<void> {
		await claimClient().releaseSubdomain({});
	}
}
